import { useEffect, useRef, useState } from 'react';

export type FanBuddyStepScrollKey = 'step-age' | 'step-gender' | 'step-region';

interface UseStepAutoScrollOptions {
  offset?: number;
  behavior?: ScrollBehavior;
}

const STEP_TARGETS: Record<
  FanBuddyStepScrollKey,
  {
    minActiveStep: number;
  }
> = {
  'step-age': { minActiveStep: 1 },
  'step-gender': { minActiveStep: 2 },
  'step-region': { minActiveStep: 3 }
};

export const useStepAutoScroll = (
  activeStep: number,
  { offset = 24, behavior = 'smooth' }: UseStepAutoScrollOptions = {}
) => {
  const [pendingScrollStepKey, setPendingScrollStepKey] = useState<FanBuddyStepScrollKey | null>(
    null
  );

  const stepAgeRef = useRef<HTMLDivElement | null>(null);
  const stepGenderRef = useRef<HTMLDivElement | null>(null);
  const stepRegionRef = useRef<HTMLDivElement | null>(null);

  const requestScrollToStep = (stepKey: FanBuddyStepScrollKey) => {
    setPendingScrollStepKey(stepKey);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!pendingScrollStepKey) return;

    const targetMeta = STEP_TARGETS[pendingScrollStepKey];
    if (!targetMeta) {
      setPendingScrollStepKey(null);
      return;
    }
    if (activeStep < targetMeta.minActiveStep) return;

    const el =
      pendingScrollStepKey === 'step-age'
        ? stepAgeRef.current
        : pendingScrollStepKey === 'step-gender'
          ? stepGenderRef.current
          : stepRegionRef.current;

    if (!el) return;

    // 렌더/애니메이션 직후 자연스럽게 "살짝 아래"로 이동
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const top = el.getBoundingClientRect().top + window.scrollY - offset;
        window.scrollTo({ top, behavior });
        setPendingScrollStepKey(null);
      });
    });
  }, [activeStep, behavior, offset, pendingScrollStepKey]);

  return {
    requestScrollToStep,
    stepRefs: {
      stepAgeRef,
      stepGenderRef,
      stepRegionRef
    }
  };
};
